import FeatureGates from '@atlaskit/feature-gate-js-client';
import { getRovoParams } from '@atlaskit/rovo-triggers';
import { createResource } from 'react-resource-router/resources';

import { logMessage } from '@townsquare/logging';
import { HomeResourceContext } from '@townsquare/router';
import {
  ROVO_TEAM25_ROLLOUT_PERFORMANCE_EXP,
  ROVO_TEAM25_ROLLOUT_PERF_B3A_EXP,
} from '@townsquare/stat-sig/experiments';
import { isGateEnabled } from '@townsquare/stat-sig/gate';

import { EntitlementAPIResponse, EntitlementV2APIResponse, RovoEntitlement } from './types';

const ROVO_PRODUCT_HEADER = 'rovo';
const ROVO_EXPERIENCE_ID_HEADER = 'ai-mate';

export const getTraceIdFromResponse = (response: Response | undefined) =>
  response?.headers?.get('x-trace-id') ??
  response?.headers?.get('atl-traceid') ??
  response?.headers?.get('x-b3-traceid') ??
  undefined;

// exported for testing
export const overrideRovoEnabledBasedOnExperiment = ({ isRovoEnabled, isRovoLLMEnabled, reason }: RovoEntitlement) => {
  // if Rovo is not enabled, we don't need to check the experiment
  if (!isRovoEnabled) {
    return { isRovoEnabled, isRovoLLMEnabled, reason };
  }

  // If a user is in the Control cohort we're overriding the entitlement check with false to NOT serve Rovo experience.
  // Test cohort: users who get the new Rovo experience -> do NOT override the TCS entitlement.
  // Control cohort: users who get the legacy experience (the baseline) -> override the TCS entitlement with false.
  const cohort = FeatureGates.getExperimentValue(ROVO_TEAM25_ROLLOUT_PERFORMANCE_EXP, 'cohort', 'test') as
    | 'test'
    | 'control';

  if (cohort === 'control') {
    return {
      isRovoEnabled: false,
      isRovoLLMEnabled: false,
      reason: 'Overriden by rovo_team25_rollout_performance_exp; in control cohort',
    };
  }

  // A repeat of the above as a second expeirmenet
  // Tenants should only be in one or the other, controlled by the targetting gate
  const cohortB3A = FeatureGates.getExperimentValue(ROVO_TEAM25_ROLLOUT_PERF_B3A_EXP, 'cohort', 'test') as
    | 'test'
    | 'control';

  if (cohortB3A === 'control') {
    return {
      isRovoEnabled: false,
      isRovoLLMEnabled: false,
      reason: 'Overriden by rovo_team25_rollout_perf_b3a_exp; in control cohort',
    };
  }

  return { isRovoEnabled, isRovoLLMEnabled, reason };
};

const getRovoEntitlement = async (cloudId: string) => {
  try {
    const response = await fetch('/gateway/api/assist/rovo/v2/entitlement', {
      headers: {
        'X-Cloudid': cloudId ?? '',
      },
    });

    if (!response.ok) {
      const responseErrorMessage = await response.text().catch(() => undefined);
      logMessage('Failed to fetch RovoEntitlement [response not ok]', 'error', {
        cloudId,
        responseStatus: response.status,
        responseTraceId: getTraceIdFromResponse(response),
        responseErrorMessage,
      });

      return {
        isRovoEnabled: false,
        isRovoLLMEnabled: false,
        reason: 'Failed to fetch RovoEntitlement [response not ok]',
      };
    }

    const data: EntitlementV2APIResponse = await response.json();

    const { isRovoEnabled, isRovoLLMEnabled, reason } = overrideRovoEnabledBasedOnExperiment({
      isRovoEnabled: data.isRovoEnabled,
      isRovoLLMEnabled: data.isRovoLLMEnabled,
      reason: data.reason,
    });

    return {
      isRovoEnabled,
      isRovoLLMEnabled,
      reason,
    };
  } catch (error) {
    logMessage('Failed to fetch RovoEntitlement [catch block]', 'error', {
      cloudId,
      error,
    });

    return {
      isRovoEnabled: false,
      isRovoLLMEnabled: false,
      reason: 'Failed to fetch RovoEntitlement [catch block]',
    };
  }
};

export const fetchRovoEntitlement = async (cloudId: string | undefined | null) => {
  if (!cloudId) {
    return {
      isRovoEnabled: false,
      isRovoLLMEnabled: false,
      reason: 'No cloudId exists',
    };
  }

  if (isGateEnabled('rovo_use_rovo_llm_enabled_check')) {
    const rovoEntitlement = await getRovoEntitlement(cloudId);
    return rovoEntitlement;
  }

  const response = await fetch('/gateway/api/assist/api/v2/configuration/entitlement', {
    headers: {
      'X-Cloudid': cloudId ?? '',
      'X-Product': ROVO_PRODUCT_HEADER,
      'X-Experience-Id': ROVO_EXPERIENCE_ID_HEADER,
    },
  });

  if (!response.ok) {
    return {
      isRovoEnabled: false,
      isRovoLLMEnabled: false,
      reason: 'Failed to fetch entitlement data',
    };
  }

  const data: EntitlementAPIResponse = await response.json();

  return {
    isRovoEnabled: data.enabled,
    // this is the same as above, for safely rollout new isRovoLLMEnabled check
    isRovoLLMEnabled: data.enabled,
    reason: data.reason,
  };
};

export const rovoEntitlementResource = createResource<RovoEntitlement>({
  getData: async (_, resourceContext) => {
    const context = resourceContext as HomeResourceContext;
    const rovoParams = getRovoParams();
    // Workspace doesn't always exist on Atlassian Home **TODAY**, if we are rendering chat without a workspace, we need to default to the first cloudId (as they may still have sites)
    const cloudId = rovoParams.cloudId ?? context.collabContext?.rovoWorkspace;

    return await fetchRovoEntitlement(cloudId);
  },
  getKey: () => 'rovo-entitlement-resource-key',
  type: 'ROVO_ENTITLEMENT',
  maxAge: 60_000 * 30, // 30 minutes cache during the SPA session
});
